import React, { useContext, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import "../../css/ofertas.css";
import { ENDPOINTS } from "../../config/api";
import { useNavigate } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";

const Profile = () => {
  const { user, token, setUser, authFetch, logout } =
    useContext(AuthContext);

  const navigate = useNavigate();

  /* ================= ESTADOS ================= */
  const [email, setEmail] = useState(user?.email || "");
  const [editing, setEditing] = useState(false);
  const [message, setMessage] = useState("");

  // Password
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // 2FA
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [showDisable2FAModal, setShowDisable2FAModal] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaMessage, setMfaMessage] = useState("");

  /* ================= SEGURIDAD ================= */
  if (!token) {
    logout();
    return <p>Redirigiendo...</p>;
  }

  if (!user) return <p>Cargando usuario...</p>;

  /* ================= EMAIL ================= */
  const handleSaveEmail = async () => {
    const data = await authFetch(ENDPOINTS.updateEmail, {
      method: "POST",
      body: JSON.stringify({ email }),
    });

    if (!data) return;

    if (data.success) {
      setUser((prev) => ({ ...prev, email }));
      setEditing(false);
      setMessage("Email actualizado correctamente");
      setTimeout(() => setMessage(""), 4000);
    } else {
      setMessage(data.message || "Error al actualizar email");
    }
  };

  /* ================= PASSWORD ================= */
  const handleChangePassword = async () => {
    if (!currentPassword) {
      setPasswordMessage("Debes ingresar tu contraseña actual");
      return;
    }

    if (!newPassword || newPassword.length < 6) {
      setPasswordMessage("La nueva contraseña debe tener al menos 6 caracteres");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMessage("Las contraseñas no coinciden");
      return;
    }

    const data = await authFetch(ENDPOINTS.updatePassword, {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    if (!data) return;

    if (data.success) {
      setPasswordMessage("Contraseña actualizada correctamente");

      setTimeout(() => {
        setShowPasswordModal(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordMessage("");
      }, 800);
    } else {
      setPasswordMessage(data.message || "Error al actualizar contraseña");
    }
  };

  /* ================= 2FA ================= */
  const handleEnable2FA = async () => {
    const data = await authFetch(ENDPOINTS.enable2FA, { method: "POST" });

    if (!data) return;

    if (data.success) {
      setMfaMessage("Se envió un código a tu correo electrónico");
      setShowMfaModal(true);
    } else {
      setMfaMessage(data.message || "Error al activar 2FA");
      setTimeout(() => setMfaMessage(""), 4000);
    }
  };

  const handleVerify2FA = async () => {
    if (!mfaCode) {
      setMfaMessage("Debes ingresar el código recibido");
      return;
    }

    const data = await authFetch(ENDPOINTS.verify2FA_login, {
      method: "POST",
      body: JSON.stringify({
        id: user.IdDocumento,
        code: mfaCode.toString().padStart(6, "0"),
      }),
    });

    if (!data) return;

    if (data.success) {
      setUser((prev) => ({ ...prev, mfa_enabled: 1 }));
      setShowMfaModal(false);
      setMfaCode("");
      setMfaMessage("");
    } else {
      setMfaMessage(data.message || "Código incorrecto");
    }
  };

  const handleDisable2FA = async () => {
    const data = await authFetch(ENDPOINTS.disable2FA, { method: "POST" });

    if (!data) return;

    if (data.success) {
      setUser((prev) => ({ ...prev, mfa_enabled: 0 }));
      setShowDisable2FAModal(false);
    } else {
      setMfaMessage(data.message || "Error al desactivar 2FA");
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="container mt-4 p-4">
      <h2>Perfil de Usuario</h2>

      {/* ================= HOJA DE VIDA ================= */}
      <div className="card mt-3 mb-4 shadow-sm">
        <div className="card-body d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-1">
              <i className="bi bi-bookmark-check me-2"></i>
              Curriculum
            </h5>
            <small className="text-muted">
              Consulta y actualiza tu información profesional
            </small>
          </div>

          <button
            className="btn btn-outline-purple"
            onClick={() => navigate("/session/curriculum/mycurriculum")}
          >
            Ver Hoja de Vida
          </button>
        </div>
      </div>

      {message && <div className="alert alert-info">{message}</div>}
      {mfaMessage && <div className="alert alert-warning">{mfaMessage}</div>}

      {/* ================= TABLA ================= */}
      <table className="table table-bordered mt-3">
        <tbody>
          <tr><th className="bg-light">Tipo documento</th><td>{user.type_doc}</td></tr>
          <tr><th className="bg-light">Documento</th><td>{user.IdDocumento}</td></tr>
          <tr><th className="bg-light">Primer nombre</th><td>{user.first_name}</td></tr>
          <tr><th className="bg-light">Segundo nombre</th><td>{user.second_name}</td></tr>
          <tr><th className="bg-light">Primer apellido</th><td>{user.last_name}</td></tr>
          <tr><th className="bg-light">Segundo apellido</th><td>{user.second_last_name}</td></tr>
          <tr>
            <th className="bg-light">Correo electrónico</th>
            <td>
              {editing ? (
                <input
                  type="email"
                  className="form-control"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              ) : email}
            </td>
          </tr>
          <tr><th className="bg-light">Rol</th><td>{user.role}</td></tr>
          <tr>
            <th className="bg-light">2FA</th>
            <td>
              {user.mfa_enabled === 1
                ? <span className="text-success fw-bold">Activado</span>
                : <span className="text-danger fw-bold">Desactivado</span>}
            </td>
          </tr>
        </tbody>
      </table>

      {editing ? (
        <div className="mb-3">
          <button className="btn btn-success me-2" onClick={handleSaveEmail}>Guardar</button>
          <button className="btn btn-secondary" onClick={() => setEditing(false)}>Cancelar</button>
        </div>
      ) : (
        <button className="btn btn-secondary mb-3" onClick={() => setEditing(true)}>
          Editar Email
        </button>
      )}

      <div className="mb-3">
        <button
          className="btn btn-outline-pink me-2"
          onClick={() => setShowPasswordModal(true)}
        >
          Cambiar Contraseña
        </button>

        {user.mfa_enabled === 1 ? (
          <button
            className="btn btn-outline-purple"
            onClick={() => setShowDisable2FAModal(true)}
          >
            Desactivar Verificación 2FA
          </button>
        ) : (
          <button className="btn btn-outline-purple" onClick={handleEnable2FA}>
            Activar Verificación 2FA
          </button>
        )}
      </div>

      {/* ================= MODALES ================= */}

      {/* CAMBIAR PASSWORD */}
{showPasswordModal && (
  <>
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">

          <div className="modal-header">
            <h5 className="modal-title">Cambiar Contraseña</h5>
            <button
              className="btn-close"
              onClick={() => setShowPasswordModal(false)}
            />
          </div>

          <div className="modal-body">
            {passwordMessage && (
              <div className="alert alert-info">{passwordMessage}</div>
            )}

            <label className="mt-2">Contraseña actual</label>
            <div className="input-group">
              <input
                type={showCurrent ? "text" : "password"}
                className="form-control"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowCurrent(!showCurrent)}
              >
                {showCurrent ? "Ocultar" : "Mostrar"}
              </button>
            </div>

            <label className="mt-3">Nueva contraseña</label>
            <div className="input-group">
              <input
                type={showNew ? "text" : "password"}
                className={`form-control ${
                  newPassword && newPassword.length < 6 ? "is-invalid" : ""
                }`}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? "Ocultar" : "Mostrar"}
              </button>
            </div>

            <label className="mt-3">Confirmar nueva contraseña</label>
            <div className="input-group">
              <input
                type={showConfirm ? "text" : "password"}
                className={`form-control ${
                  confirmPassword && confirmPassword !== newPassword
                    ? "is-invalid"
                    : ""
                }`}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              <button
                className="btn btn-outline-secondary"
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? "Ocultar" : "Mostrar"}
              </button>
            </div>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setShowPasswordModal(false)}
            >
              Cancelar
            </button>
            <button className="btn btn-outline-pink" onClick={handleChangePassword}>
              Guardar
            </button>
          </div>

        </div>
      </div>
    </div>

    <div className="modal-backdrop fade show"></div>
  </>
)}


      {/* ACTIVAR 2FA */}
{showMfaModal && (
  <>
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Activar Verificación 2FA</h5>
            <button
              className="btn-close"
              onClick={() => setShowMfaModal(false)}
            />
          </div>

          <div className="modal-body">
            {mfaMessage && <div className="alert alert-info">{mfaMessage}</div>}
            <label>Ingresa el código enviado a tu correo</label>
            <input
              type="text"
              className="form-control"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
            />
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-secondary"
              onClick={() => setShowMfaModal(false)}
            >
              Cancelar
            </button>
            <button className="btn btn-primary" onClick={handleVerify2FA}>
              Verificar
            </button>
          </div>
        </div>
      </div>
    </div>

    <div className="modal-backdrop fade show"></div>
  </>
)}


      {/* DESACTIVAR 2FA */}
{showDisable2FAModal && (
  <>
    <div className="modal fade show d-block" tabIndex="-1">
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Confirmar Desactivación 2FA</h5>
            <button
              type="button"
              className="btn-close"
              onClick={() => setShowDisable2FAModal(false)}
            />
          </div>

          <div className="modal-body">
            <p>
              ¿Estás seguro de que deseas desactivar la verificación en dos pasos (2FA)?
              Esto podría reducir la seguridad de tu cuenta.
            </p>
          </div>

          <div className="modal-footer">
            <button
              className="btn btn-outline-purple"
              onClick={() => setShowDisable2FAModal(false)}
            >
              Cancelar
            </button>
            <button
              className="btn btn-outline-pink"
              onClick={() => {
                handleDisable2FA();
                setShowDisable2FAModal(false);
              }}
            >
              Desactivar 2FA
            </button>
          </div>
        </div>
      </div>
    </div>

    <div className="modal-backdrop fade show"></div>
  </>
)}

    </div>
  );
};

export default Profile;
